import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Package,
  Building2,
  Home,
  Shirt,
  Calendar,
  User,
  Weight,
  RefreshCw,
  Printer,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';

const EntregasRecolectadas = ({ entregas, isLoading, onRefresh }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [printingId, setPrintingId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleReimprimir = async (entrega) => {
    setPrintingId(entrega._id);
    try {
      const printer = getPrinterInstance();
      // Mismo ticket que se genera al confirmar la recolección (2 copias)
      await printer.printRecoleccionDobleCopia({
        consecutivo: entrega.consecutivo_recoleccion || entrega.solicitud_id,
        fecha: entrega.fecha_entrega || entrega.createdAt,
        edificio: entrega.edificio,
        hospedaje: entrega.hospedaje,
        receptor_nombre: entrega.receptor_nombre,
        conductor: entrega.conductor || entrega.recolector,
        items: entrega.items,
        peso_real: entrega.peso_real,
        peso_total: entrega.peso_total,
      });
      toast.success('Comprobante reimpreso correctamente (2 copias)');
    } catch (error) {
      console.error('Error al reimprimir:', error);
      toast.error(error.message || 'Error al reimprimir el comprobante');
    } finally {
      setPrintingId(null);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTotalItems = (items) => {
    if (!items || !Array.isArray(items)) return 0;
    return items.reduce((sum, item) => sum + (item.cantidad || 0), 0);
  };

  const getTotalPeso = (items) => {
    if (!items || !Array.isArray(items)) return 0;
    return items.reduce((sum, item) => {
      const peso = item.peso || 0;
      const cantidad = item.cantidad || 0;
      return sum + (peso * cantidad);
    }, 0);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
        <p className="text-gray-500">Cargando entregas recolectadas...</p>
      </div>
    );
  }

  if (!entregas || entregas.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Package className="h-16 w-16 text-gray-300 mb-4" />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          No hay entregas recolectadas hoy
        </h3>
        <p className="text-gray-500 text-center mb-4">
          Las entregas recolectadas del día aparecerán aquí
        </p>
        <Button onClick={onRefresh} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Entregas Recolectadas</h2>
          <p className="text-sm text-gray-500">
            {entregas.length} entrega{entregas.length !== 1 ? 's' : ''} recolectada{entregas.length !== 1 ? 's' : ''} hoy
          </p>
        </div>
        <Button onClick={onRefresh} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Lista de entregas */}
      <div className="space-y-3">
        {entregas.map((entrega) => {
          const isExpanded = expandedId === entrega._id;
          const totalItems = getTotalItems(entrega.items);
          const totalPeso = getTotalPeso(entrega.items);

          return (
            <motion.div
              key={entrega._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="border-2 border-purple-200 bg-purple-50">
                <CardContent className="p-4">
                  {/* Header de la entrega */}
                  <div
                    className="flex items-start justify-between cursor-pointer"
                    onClick={() => toggleExpand(entrega._id)}
                  >
                    <div className="flex-1">
                      {/* Consecutivo y Edificio */}
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="h-5 w-5 text-purple-600" />
                        <span className="font-semibold text-gray-900">
                          {entrega.edificio?.nombre || 'Edificio desconocido'}
                        </span>
                        <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
                          {entrega.logistica === 'vertical' ? 'Vertical' : 'Horizontal'}
                        </Badge>
                        {entrega.consecutivo_recoleccion && (
                          <Badge className="bg-purple-600 text-white">
                            {entrega.consecutivo_recoleccion}
                          </Badge>
                        )}
                      </div>

                      {/* Hospedaje (si es horizontal) */}
                      {entrega.logistica === 'horizontal' && entrega.hospedaje && (
                        <div className="flex items-center gap-2 mb-2 ml-7">
                          <Home className="h-4 w-4 text-purple-500" />
                          <span className="text-sm text-gray-700">
                            {entrega.hospedaje.nombrePropiedad}
                          </span>
                        </div>
                      )}

                      {/* Información resumida */}
                      <div className="flex flex-wrap items-center gap-3 ml-7 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Shirt className="h-4 w-4" />
                          <span>{totalItems} prendas</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Weight className="h-4 w-4" />
                          <span>{totalPeso.toFixed(2)}g</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(entrega.createdAt)}</span>
                        </div>
                      </div>

                      {/* Recolector */}
                      {entrega.recolector && (
                        <div className="flex items-center gap-2 mt-2 ml-7">
                          <User className="h-4 w-4 text-purple-500" />
                          <span className="text-sm text-gray-600">
                            Recolectado por: {entrega.recolector.nombre}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Indicador de expansión */}
                    <div className="ml-2">
                      <motion.div
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <svg
                          className="h-5 w-5 text-purple-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </motion.div>
                    </div>
                  </div>

                  {/* Detalle de items (expandible) */}
                  {isExpanded && entrega.items && entrega.items.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 pt-4 border-t border-purple-200"
                    >
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">
                        Detalle de prendas:
                      </h4>
                      <div className="space-y-2">
                        {entrega.items.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 bg-white rounded border border-purple-100"
                          >
                            <div className="flex items-center gap-2">
                              <Shirt className="h-4 w-4 text-purple-500" />
                              <span className="text-sm font-medium text-gray-700">
                                {item.nombre || 'Prenda sin nombre'}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span>{item.cantidad || 0} unidades</span>
                              <span className="text-gray-400">
                                {((item.peso || 0) * (item.cantidad || 0)).toFixed(2)}g
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Total */}
                      <div className="mt-3 pt-3 border-t border-purple-200 flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-700">Total:</span>
                        <div className="flex items-center gap-4 text-sm font-semibold text-gray-900">
                          <span>{totalItems} prendas</span>
                          <span>{totalPeso.toFixed(2)}g</span>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Botón reimprimir */}
                  <Button
                    variant="outline"
                    className="w-full mt-3 bg-white"
                    size="sm"
                    onClick={() => handleReimprimir(entrega)}
                    disabled={printingId === entrega._id}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    {printingId === entrega._id ? 'Imprimiendo...' : 'Reimprimir Comprobante'}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default EntregasRecolectadas;
